import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useSession } from '~/features/account/SessionProvider';
import { Button, classes } from './ui';

/**
 * The frame every page renders inside.
 *
 * Navigation adapts to the session: visitors see the catalog, signed-in users see installations,
 * developers see publishing. The account menu keeps profile and sign-out discoverable without
 * crowding the bar.
 */
export function AppShell(): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col">
      <TopBar />
      <main className="relative mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 sm:py-10">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function TopBar(): JSX.Element {
  const { isSignedIn, isDeveloper, session, signOut } = useSession();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-surface-0/80 backdrop-blur-xl">
      <div className="mx-auto flex w-full max-w-6xl items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6">
        <Link
          to="/"
          className="group flex shrink-0 items-center gap-2.5 font-display font-bold tracking-tight text-slate-50"
        >
          <span
            aria-hidden="true"
            className="grid h-8 w-8 place-items-center rounded-xl bg-gradient-to-br from-brand to-brand-dim text-sm text-slate-950 shadow-glow transition-transform duration-300 ease-soft group-hover:scale-105"
          >
            ◆
          </span>
          <span className="hidden sm:inline">
            WorkerHub
            <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              on Cloudflare
            </span>
          </span>
        </Link>

        <form
          role="search"
          className="min-w-0 flex-1"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = query.trim();
            navigate(
              trimmed === '' ? '/applications' : `/applications?q=${encodeURIComponent(trimmed)}`,
            );
            setMenuOpen(false);
          }}
        >
          <label className="sr-only" htmlFor="global-search">
            Search applications
          </label>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
            >
              ⌕
            </span>
            <input
              id="global-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search Workers, APIs, tools…"
              className="input pl-9"
            />
          </div>
        </form>

        <nav className="hidden items-center gap-1 rounded-full border border-slate-800/80 bg-slate-950/40 p-1 md:flex">
          <TopBarLink to="/applications">Browse</TopBarLink>
          {isSignedIn ? <TopBarLink to="/installations">Installed</TopBarLink> : null}
          {isDeveloper ? <TopBarLink to="/developer">Publish</TopBarLink> : null}
        </nav>

        <div className="hidden shrink-0 sm:block">
          {isSignedIn && session !== undefined ? (
            <AccountMenu
              displayName={session.displayName}
              email={session.email}
              isDeveloper={isDeveloper}
              onSignOut={() => {
                void signOut();
              }}
            />
          ) : (
            <Link to="/signin">
              <Button variant="primary">Sign in</Button>
            </Link>
          )}
        </div>

        <button
          type="button"
          className="grid h-10 w-10 place-items-center rounded-xl border border-slate-800 text-slate-300 hover:bg-slate-800 md:hidden"
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="sr-only">{menuOpen ? 'Close menu' : 'Open menu'}</span>
          <span aria-hidden="true" className="text-lg leading-none">
            {menuOpen ? '×' : '☰'}
          </span>
        </button>
      </div>

      {menuOpen ? (
        <nav
          id="mobile-nav"
          className="animate-fade-in border-t border-slate-800 px-4 py-4 md:hidden sm:px-6"
        >
          <div className="flex flex-col gap-1">
            <TopBarLink to="/applications" onNavigate={() => setMenuOpen(false)}>
              Browse
            </TopBarLink>
            {isSignedIn ? (
              <TopBarLink to="/installations" onNavigate={() => setMenuOpen(false)}>
                Installed
              </TopBarLink>
            ) : null}
            {isDeveloper ? (
              <TopBarLink to="/developer" onNavigate={() => setMenuOpen(false)}>
                Publish
              </TopBarLink>
            ) : null}
            {isSignedIn ? (
              <>
                <TopBarLink to="/account" onNavigate={() => setMenuOpen(false)}>
                  Account
                </TopBarLink>
                {!isDeveloper ? (
                  <TopBarLink to="/developer/register" onNavigate={() => setMenuOpen(false)}>
                    Become a publisher
                  </TopBarLink>
                ) : null}
                <Button
                  variant="ghost"
                  className="mt-2 justify-start"
                  onClick={() => {
                    setMenuOpen(false);
                    void signOut();
                  }}
                >
                  Sign out
                </Button>
              </>
            ) : (
              <Link to="/signin" onClick={() => setMenuOpen(false)} className="mt-3 block">
                <Button variant="primary" className="w-full">
                  Sign in with Cloudflare
                </Button>
              </Link>
            )}
          </div>
        </nav>
      ) : null}
    </header>
  );
}

function AccountMenu({
  displayName,
  email,
  isDeveloper,
  onSignOut,
}: {
  displayName: string;
  email: string;
  isDeveloper: boolean;
  onSignOut: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-full border border-slate-700/80 bg-slate-900/70 py-1 pl-1 pr-3 transition-colors hover:border-slate-600 hover:bg-slate-800"
      >
        <span
          aria-hidden="true"
          className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-brand/90 to-brand-dim text-xs font-bold text-slate-950"
        >
          {initials(displayName)}
        </span>
        <span className="max-w-[8rem] truncate text-left text-sm font-medium text-slate-100">
          {displayName}
        </span>
        <span aria-hidden="true" className="text-slate-500">
          ▾
        </span>
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 z-40 mt-2 w-72 animate-fade-in overflow-hidden rounded-2xl border border-slate-700/80 bg-surface-1/95 shadow-panel backdrop-blur-xl"
        >
          <div className="border-b border-slate-800 px-4 py-3">
            <p className="truncate font-semibold text-slate-100">{displayName}</p>
            <p className="truncate font-mono text-xs text-slate-500">{email}</p>
          </div>
          <div className="p-1.5">
            <MenuLink to="/account" onNavigate={() => setOpen(false)}>
              Account &amp; security
            </MenuLink>
            <MenuLink to="/installations" onNavigate={() => setOpen(false)}>
              My installations
            </MenuLink>
            {isDeveloper ? (
              <MenuLink to="/developer" onNavigate={() => setOpen(false)}>
                Publishing console
              </MenuLink>
            ) : (
              <MenuLink to="/developer/register" onNavigate={() => setOpen(false)}>
                Become a publisher
              </MenuLink>
            )}
          </div>
          <div className="border-t border-slate-800 p-1.5">
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center rounded-xl px-3 py-2.5 text-left text-sm text-red-300 transition-colors hover:bg-red-500/10"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MenuLink({
  to,
  children,
  onNavigate,
}: {
  to: string;
  children: string;
  onNavigate: () => void;
}): JSX.Element {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onNavigate}
      className="flex items-center rounded-xl px-3 py-2.5 text-sm text-slate-200 transition-colors hover:bg-slate-800/80"
    >
      {children}
    </Link>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

function TopBarLink({
  to,
  children,
  onNavigate,
}: {
  to: string;
  children: string;
  onNavigate?: () => void;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        classes(
          'nav-pill',
          isActive
            ? 'bg-slate-800 text-slate-50 shadow-sm shadow-black/20'
            : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-100',
        )
      }
    >
      {children}
    </NavLink>
  );
}

function Footer(): JSX.Element {
  return (
    <footer className="mt-8 border-t border-slate-800/80 bg-surface-1/40">
      <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 py-10 sm:grid-cols-[1.4fr_1fr_1fr] sm:px-6">
        <div>
          <p className="font-display text-sm font-bold text-slate-100">WorkerHub</p>
          <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
            Every listing is analyzed before publication. Permissions, resources, and outbound
            services are stated up front — before you install.
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Explore</p>
          <ul className="mt-3 space-y-2 text-sm text-slate-400">
            <li>
              <Link to="/applications" className="hover:text-slate-200">
                Browse applications
              </Link>
            </li>
            <li>
              <Link to="/applications?sort=popular" className="hover:text-slate-200">
                Most installed
              </Link>
            </li>
            <li>
              <Link to="/signin" className="hover:text-slate-200">
                Sign in with Cloudflare
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Publish</p>
          <ul className="mt-3 space-y-2 text-sm text-slate-400">
            <li>
              <Link to="/payments" className="hover:text-slate-200">
                Crypto payments
              </Link>
            </li>
            <li>
              <Link to="/publish" className="hover:text-slate-200">
                Publisher guide
              </Link>
            </li>
            <li>
              <Link to="/developer/register" className="hover:text-slate-200">
                Become a publisher
              </Link>
            </li>
            <li>
              <Link to="/developer" className="hover:text-slate-200">
                Publishing console
              </Link>
            </li>
            <li>
              <Link to="/account" className="hover:text-slate-200">
                Account &amp; connections
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
